import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useReducedMotion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { DotLottie } from "@lottiefiles/dotlottie-web";
import type { OverlayConfig, OverlaySizeMode } from "../types/narrative";
import { useContainerSize, useViewportSize } from "../hooks/useContainerSize";
import {
  getLottieDimensions,
  parseOffsetValue,
  fetchLottieJson,
  colorizeLottie,
  isDotLottie,
  type LottieDimensions,
} from "../utils/lottieHelpers";

interface AnimationOverlayProps {
  config: OverlayConfig;
  className?: string;
}

interface CalculatedSize {
  width: number;
  height: number;
}

/**
 * Calculate the final overlay size based on sizeMode, container, and constraints
 */
function calculateSize(
  sizeMode: OverlaySizeMode,
  containerWidth: number,
  containerHeight: number,
  lottieDims: LottieDimensions | null,
  config: OverlayConfig,
): CalculatedSize {
  const { sizePercent, maxSize, minSize } = config;

  // Calculate base dimensions based on sizeMode
  let width: number;
  let height: number;

  // If sizePercent is provided, use it as the base
  if (sizePercent !== undefined) {
    // Normalize: number shorthand means equal width & height percentage
    const percentConfig =
      typeof sizePercent === "number"
        ? { width: sizePercent, height: sizePercent }
        : sizePercent;

    const targetWidth =
      percentConfig.width !== undefined
        ? (percentConfig.width / 100) * containerWidth
        : containerWidth;
    const targetHeight =
      percentConfig.height !== undefined
        ? (percentConfig.height / 100) * containerHeight
        : containerHeight;

    switch (sizeMode) {
      case "contain": {
        // Fit within target bounds while preserving aspect ratio
        const widthRatio = targetWidth / (lottieDims?.width ?? targetWidth);
        const heightRatio = targetHeight / (lottieDims?.height ?? targetHeight);
        const scale = Math.min(widthRatio, heightRatio);
        width = (lottieDims?.width ?? targetWidth) * scale;
        height = (lottieDims?.height ?? targetHeight) * scale;
        break;
      }
      case "cover": {
        // Fill target bounds while preserving aspect ratio (may crop)
        const widthRatio = targetWidth / (lottieDims?.width ?? targetWidth);
        const heightRatio = targetHeight / (lottieDims?.height ?? targetHeight);
        const scale = Math.max(widthRatio, heightRatio);
        width = (lottieDims?.width ?? targetWidth) * scale;
        height = (lottieDims?.height ?? targetHeight) * scale;
        break;
      }
      case "fill": {
        // Stretch to fill (ignores aspect ratio)
        width = targetWidth;
        height = targetHeight;
        break;
      }
      case "auto":
      default: {
        // Use intrinsic dimensions scaled to fit within percent bounds
        if (lottieDims) {
          const widthRatio = targetWidth / lottieDims.width;
          const heightRatio = targetHeight / lottieDims.height;
          const scale = Math.min(widthRatio, heightRatio);
          width = lottieDims.width * scale;
          height = lottieDims.height * scale;
        } else {
          width = targetWidth;
          height = targetHeight;
        }
        break;
      }
    }
  } else {
    // No sizePercent, use container dimensions directly
    switch (sizeMode) {
      case "contain": {
        if (lottieDims) {
          const widthRatio = containerWidth / lottieDims.width;
          const heightRatio = containerHeight / lottieDims.height;
          const scale = Math.min(widthRatio, heightRatio);
          width = lottieDims.width * scale;
          height = lottieDims.height * scale;
        } else {
          width = containerWidth;
          height = containerHeight;
        }
        break;
      }
      case "cover": {
        if (lottieDims) {
          const widthRatio = containerWidth / lottieDims.width;
          const heightRatio = containerHeight / lottieDims.height;
          const scale = Math.max(widthRatio, heightRatio);
          width = lottieDims.width * scale;
          height = lottieDims.height * scale;
        } else {
          width = containerWidth;
          height = containerHeight;
        }
        break;
      }
      case "fill": {
        width = containerWidth;
        height = containerHeight;
        break;
      }
      case "auto":
      default: {
        // Use Lottie intrinsic dimensions or fallback
        width = lottieDims?.width ?? 100;
        height = lottieDims?.height ?? 100;
        break;
      }
    }
  }

  // Apply min/max constraints
  if (maxSize) {
    if (maxSize.width !== undefined && width > maxSize.width) {
      const scale = maxSize.width / width;
      width = maxSize.width;
      height = height * scale;
    }
    if (maxSize.height !== undefined && height > maxSize.height) {
      const scale = maxSize.height / height;
      height = maxSize.height;
      width = width * scale;
    }
  }

  if (minSize) {
    if (minSize.width !== undefined && width < minSize.width) {
      const scale = minSize.width / width;
      width = minSize.width;
      height = height * scale;
    }
    if (minSize.height !== undefined && height < minSize.height) {
      const scale = minSize.height / height;
      height = minSize.height;
      width = width * scale;
    }
  }

  return { width, height };
}

export function AnimationOverlay({
  config,
  className = "",
}: AnimationOverlayProps) {
  const {
    src,
    type = "lottie",
    position = {},
    sizeMode = "auto",
    rotation = 0,
    rotationOffset = 0,
    offset,
    opacity = 1,
    loop = true,
    speed = 1,
    zIndex = 10,
    relativeTo = "container",
    allowOverflow = true,
    color,
    delay = 0,
    persist = false,
  } = config;

  // Reduced-motion policy for overlay Lotties:
  //  - persist === true  → render frozen on the final frame (no playback)
  //  - persist falsy     → don't render the animation at all
  // Applies regardless of the config level (section/group/item) since every
  // overlay flows through this component.
  const prefersReducedMotion = useReducedMotion();
  const freezeOnLastFrame = Boolean(prefersReducedMotion && persist);
  const skipEntirely = Boolean(prefersReducedMotion && !persist);

  // Derive loop behavior: true = infinite, false/0 = once, number = N times
  const loopCount = typeof loop === "number" ? loop : loop ? Infinity : 1;
  const shouldLoop = loopCount === Infinity;

  // Track when a non-looping animation finishes
  const [completed, setCompleted] = useState(false);
  const iterationRef = useRef(0);

  // Handle delay before rendering
  const [shouldRender, setShouldRender] = useState(delay === 0);

  useEffect(() => {
    if (delay > 0) {
      const timer = setTimeout(() => setShouldRender(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay]);

  // Track container and viewport sizes
  const [containerRef, containerSize] = useContainerSize();
  const viewportSize = useViewportSize();

  // Determine which size to use based on relativeTo
  const effectiveSize =
    relativeTo === "viewport" ? viewportSize : containerSize;

  // Fetch Lottie dimensions (async with caching for .json, from player for .lottie)
  const [lottieDims, setLottieDims] = useState<LottieDimensions | null>(null);

  // For .json files with color override, store colorized JSON object for the `data` prop
  const [colorizedData, setColorizedData] = useState<Record<
    string,
    unknown
  > | null>(null);

  // Read dimensions from the DotLottie player instance (works for both .json and .lottie)
  const prevDotLottieRef = useRef<DotLottie | null>(null);
  const onLoadRef = useRef<(() => void) | null>(null);

  const onCompleteRef = useRef<(() => void) | null>(null);
  const onLoopRef = useRef<(() => void) | null>(null);

  const dotLottieRefCallback = useCallback(
    (dotLottie: DotLottie | null) => {
      // Clean up previous instance listeners
      if (prevDotLottieRef.current) {
        if (onLoadRef.current)
          prevDotLottieRef.current.removeEventListener(
            "load",
            onLoadRef.current,
          );
        if (onCompleteRef.current)
          prevDotLottieRef.current.removeEventListener(
            "complete",
            onCompleteRef.current,
          );
        if (onLoopRef.current)
          prevDotLottieRef.current.removeEventListener(
            "loop",
            onLoopRef.current,
          );
      }

      if (dotLottie) {
        const onLoad = () => {
          const size = dotLottie.animationSize();
          if (size && size.width > 0 && size.height > 0) {
            setLottieDims({
              width: size.width,
              height: size.height,
              aspectRatio: size.width / size.height,
            });
          }
          // Reduced motion + persist: freeze on the final frame instead of playing.
          // (setFrame seeks *and* renders, so no extra render call is needed.)
          if (freezeOnLastFrame) {
            dotLottie.setLoop(false);
            dotLottie.setFrame(Math.max(0, dotLottie.totalFrames - 1));
          }
        };
        dotLottie.addEventListener("load", onLoad);
        onLoadRef.current = onLoad;

        if (!shouldLoop && !freezeOnLastFrame) {
          // For finite iterations: listen for complete to unmount
          const onComplete = () => setCompleted(true);
          dotLottie.addEventListener("complete", onComplete);
          onCompleteRef.current = onComplete;

          // For N > 1: count loop events and stop looping after N-1 iterations
          if (loopCount > 1) {
            iterationRef.current = 0;
            const onLoop = () => {
              iterationRef.current += 1;
              if (iterationRef.current >= loopCount - 1) {
                dotLottie.setLoop(false);
              }
            };
            dotLottie.addEventListener("loop", onLoop);
            onLoopRef.current = onLoop;
          } else {
            onLoopRef.current = null;
          }
        } else {
          onCompleteRef.current = null;
          onLoopRef.current = null;
        }

        prevDotLottieRef.current = dotLottie;
      } else {
        onLoadRef.current = null;
        onCompleteRef.current = null;
        onLoopRef.current = null;
        prevDotLottieRef.current = null;
      }
    },
    [shouldLoop, loopCount, freezeOnLastFrame],
  );

  // Effect 1: Reset and load dimensions when src or type change
  useEffect(() => {
    if (type === "lottie" && src) {
      setLottieDims(null);

      let cancelled = false;

      // For .json files, fetch dimensions via helper (faster, cached)
      if (!isDotLottie(src)) {
        getLottieDimensions(src).then((dims) => {
          if (!cancelled) setLottieDims(dims);
        });
      }
      // .lottie dimensions are read from the player instance via dotLottieRefCallback

      return () => {
        cancelled = true;
      };
    }
  }, [src, type]);

  // Effect 2: Handle colorization when src or color change
  useEffect(() => {
    if (type === "lottie" && src && color && !isDotLottie(src)) {
      setColorizedData(null);

      let cancelled = false;

      fetchLottieJson(src).then((json) => {
        if (!cancelled && json) {
          setColorizedData(
            colorizeLottie(json, color) as Record<string, unknown>,
          );
        }
      });

      return () => {
        cancelled = true;
      };
    } else {
      setColorizedData(null);
    }
  }, [src, type, color]);

  // Calculate final size
  const calculatedSize = useMemo(() => {
    if (effectiveSize.width === 0 || effectiveSize.height === 0) {
      // Container not measured yet, return fallback
      return { width: 100, height: 100 };
    }
    return calculateSize(
      sizeMode,
      effectiveSize.width,
      effectiveSize.height,
      lottieDims,
      config,
    );
  }, [sizeMode, effectiveSize.width, effectiveSize.height, lottieDims, config]);

  // Calculate offset values
  const offsetX = useMemo(
    () => parseOffsetValue(offset?.x, effectiveSize.width),
    [offset?.x, effectiveSize.width],
  );
  const offsetY = useMemo(
    () => parseOffsetValue(offset?.y, effectiveSize.height),
    [offset?.y, effectiveSize.height],
  );

  // Map sizeMode to DotLottie player's native layout fit so the renderer
  // preserves aspect-ratio even before intrinsic lottie dims are known.
  const dotLottieLayout = useMemo(() => {
    const fitMap = {
      cover: "cover",
      contain: "contain",
      fill: "fill",
    } as const;
    const fit = fitMap[sizeMode as keyof typeof fitMap];
    if (!fit) return undefined;
    return { fit, align: [0.5, 0.5] as [number, number] };
  }, [sizeMode]);

  // Reduced motion, non-persistent overlay: don't render the animation at all.
  if (skipEntirely) return null;

  // Don't render until delay has elapsed
  if (!shouldRender) {
    return relativeTo === "viewport" ? null : (
      <div
        ref={containerRef}
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex }}
      />
    );
  }

  // Unmount after a non-looping animation finishes — unless persist is set, in
  // which case the player stays mounted holding its final frame. Without this
  // guard persist only ever applied under reduced motion (freezeOnLastFrame),
  // so a normal-motion overlay unmounted despite asking to persist.
  if (completed && !persist) return null;

  // Build positioning styles
  const positionStyle: React.CSSProperties = {
    position: relativeTo === "viewport" ? "fixed" : "absolute",
    zIndex,
  };

  // Build transform string
  const transformParts: string[] = [];

  // For cover/contain with DotLottie native layout, fill the container and let
  // the player's layout.fit handle aspect-ratio preservation.  This avoids
  // computing oversized pixel dimensions that can fight with the player's own
  // scaling, especially before intrinsic Lottie dimensions are known.
  const usePlayerFit =
    dotLottieLayout && (sizeMode === "cover" || sizeMode === "contain");

  // If no position specified, center it (or fill for player-fit modes)
  if (!position.top && !position.bottom && !position.left && !position.right) {
    if (usePlayerFit) {
      positionStyle.inset = 0;
    } else {
      positionStyle.top = "50%";
      positionStyle.left = "50%";
      transformParts.push("translate(-50%, -50%)");
    }
  } else {
    // Apply specified positions
    if (position.top !== undefined) positionStyle.top = position.top;
    if (position.bottom !== undefined) positionStyle.bottom = position.bottom;
    if (position.left !== undefined) positionStyle.left = position.left;
    if (position.right !== undefined) positionStyle.right = position.right;
  }

  // Add offset translation
  if (offsetX !== 0 || offsetY !== 0) {
    transformParts.push(`translate(${offsetX}px, ${offsetY}px)`);
  }

  // Add rotation (base + offset)
  const totalRotation = rotation + rotationOffset;
  if (totalRotation !== 0) {
    transformParts.push(`rotate(${totalRotation}deg)`);
  }

  if (transformParts.length > 0) {
    positionStyle.transform = transformParts.join(" ");
  }

  // Build size styles — for player-fit modes, let DotLottie fill the wrapper
  const sizeStyle: React.CSSProperties = usePlayerFit
    ? { width: "100%", height: "100%" }
    : { width: calculatedSize.width, height: calculatedSize.height };

  const overlayContent = (
    <div
      className={`pointer-events-none ${className}`}
      style={{
        ...positionStyle,
        opacity,
        zIndex,
      }}
    >
      {type === "lottie" &&
        // When color is requested for a .json Lottie, wait for colorized data
        // before rendering to avoid flashing the uncolorized asset.
        (color && !isDotLottie(src) && !colorizedData ? null : (
          <DotLottieReact
            key={`${src}::${color ?? ""}`}
            autoplay={!freezeOnLastFrame}
            loop={!freezeOnLastFrame && (shouldLoop || loopCount > 1)}
            speed={speed}
            {...(colorizedData ? { data: colorizedData } : { src })}
            dotLottieRefCallback={dotLottieRefCallback}
            style={sizeStyle}
            layout={dotLottieLayout}
          />
        ))}
    </div>
  );

  // For viewport-relative rendering, use a portal
  if (relativeTo === "viewport") {
    return createPortal(overlayContent, document.body);
  }

  // For container-relative, wrap in a measurement container
  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 pointer-events-none ${allowOverflow ? "" : "overflow-hidden"}`}
      style={{ zIndex }}
    >
      {overlayContent}
    </div>
  );
}
